// VersionHistoryList - Displays version history with activate/delete actions

import { useState } from 'react';
import { useActivateVersion, useDeleteVersion } from '../../../hooks/unified/useContentVersionsQueries';
import { useToast } from '../../../contexts/ToastContext';
import { ContentPreview } from './ContentPreview';
import type { ContentComponentType, ContentVersion } from '../../../hooks/unified/useContentVersionsQueries';

interface VersionHistoryListProps {
  jobId: string;
  componentType: ContentComponentType;
  versions: ContentVersion[];
  activeVersionId?: string;
  onActivated?: (newContent: string | string[]) => void;
}

export function VersionHistoryList({
  jobId,
  componentType,
  versions,
  activeVersionId,
  onActivated,
}: VersionHistoryListProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const { showSuccess, showError, showInfo } = useToast();
  
  const activateMutation = useActivateVersion();
  const deleteMutation = useDeleteVersion();

  const handleActivate = async (version: ContentVersion) => {
    try {
      await activateMutation.mutateAsync({
        jobId,
        componentType,
        versionId: version.id,
      });

      showSuccess('Version Activated', `Version ${version.version_number} is now active.`);
      
      // Parse content for arrays
      let parsedContent: string | string[] = version.content;
      if (componentType === 'requirements' || componentType === 'benefits') {
        try {
          parsedContent = JSON.parse(version.content);
        } catch {
          // Keep as string
        }
      }
      
      onActivated?.(parsedContent);
    } catch (error) {
      showError('Activation Failed', error instanceof Error ? error.message : 'Failed to activate version');
    }
  };

  const handleDelete = async (versionId: string) => {
    try {
      await deleteMutation.mutateAsync({
        jobId,
        componentType,
        versionId,
      });

      showInfo('Version Deleted', 'The version has been removed.');
      setDeleteConfirmId(null);
    } catch (error) {
      showError('Delete Failed', error instanceof Error ? error.message : 'Failed to delete version');
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (versions.length === 0) {
    return (
      <div className="text-center py-6 text-gray-500">
        <svg
          className="w-12 h-12 mx-auto mb-3 text-gray-300"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
          />
        </svg>
        <p className="text-sm">No versions yet</p>
        <p className="text-xs text-gray-400 mt-1">Generate content to create your first version</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {versions.map((version) => {
        const isActive = version.id === activeVersionId;
        const isExpanded = expandedId === version.id;
        const isDeleting = deleteConfirmId === version.id;

        return (
          <div
            key={version.id}
            className={`border rounded-lg overflow-hidden transition-colors ${
              isActive
                ? 'border-green-200 bg-green-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            {/* Version Header */}
            <div
              className="flex flex-col sm:flex-row sm:items-center sm:justify-between px-3 sm:px-4 py-3 cursor-pointer gap-2 min-h-[48px]"
              onClick={() => setExpandedId(isExpanded ? null : version.id)}
            >
              <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                <span className="text-sm font-medium text-gray-700">
                  v{version.version_number}
                </span>
                {isActive && (
                  <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded">
                    Active
                  </span>
                )}
                <span className="text-xs text-gray-500">
                  {formatDate(version.created_at)}
                </span>
              </div>
              <div className="flex items-center gap-2">
                {!isActive && (
                  <>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleActivate(version);
                      }}
                      disabled={activateMutation.isPending}
                      className="px-3 py-2 text-xs font-medium text-purple-600 hover:bg-purple-50 
                                 rounded border border-purple-200 transition-colors disabled:opacity-50 min-h-[36px]"
                    >
                      {activateMutation.isPending ? 'Activating...' : 'Activate'}
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteConfirmId(version.id);
                      }}
                      className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors min-w-[36px] min-h-[36px] flex items-center justify-center"
                      title="Delete version"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                  </>
                )}
                <svg
                  className={`w-4 h-4 text-gray-400 transition-transform ${
                    isExpanded ? 'rotate-180' : ''
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>

            {/* Delete Confirmation */}
            {isDeleting && (
              <div className="px-3 sm:px-4 py-3 bg-red-50 border-t border-red-100">
                <p className="text-sm text-red-700 mb-2">Delete this version?</p>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleDelete(version.id)}
                    disabled={deleteMutation.isPending}
                    className="px-3 py-2 text-xs font-medium text-white bg-red-600 hover:bg-red-700 
                               rounded transition-colors disabled:opacity-50 min-h-[36px]"
                  >
                    {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                  </button>
                  <button
                    onClick={() => setDeleteConfirmId(null)}
                    className="px-3 py-2 text-xs font-medium text-gray-600 hover:bg-gray-100 
                               rounded transition-colors min-h-[36px]"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Expanded Content */}
            {isExpanded && !isDeleting && (
              <div className="px-4 py-3 border-t border-gray-100 bg-gray-50">
                {version.prompt_used && (
                  <div className="mb-2">
                    <span className="text-xs text-gray-500">Prompt: </span>
                    <span className="text-xs text-gray-600 italic">
                      {version.prompt_used.length > 100
                        ? version.prompt_used.substring(0, 100) + '...'
                        : version.prompt_used}
                    </span>
                  </div>
                )}
                <ContentPreview
                  content={version.content}
                  componentType={componentType}
                  maxLines={5}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
